<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <div
        class="target-practice focusable"
        id="target-practice-container"
        tabindex="0"
        slot="content"
      >
        <div
          v-if="progress"
          v-show="showBothHands || holdKeyPressed || oneHandedTypingHand"
          class="target-practice-container"
        >
          <div
            v-if="isVisibleLeftHand"
            :class="
              `target-practice-image-left ${oneHandedTypingHand ? 'one-hand-typing-hand' : ''}`
            "
          >
            <div class="svg-image-container current-letter">
              <svg width="200px" v-html="leftHand"></svg>
            </div>
          </div>

          <div
            v-if="isVisibleRightHand"
            :class="
              `target-practice-image-right ${oneHandedTypingHand ? 'one-hand-typing-hand' : ''}`
            "
          >
            <div class="svg-image-container current-letter">
              <svg width="200px" v-html="rightHand"></svg>
            </div>
          </div>
        </div>
      </div>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import TouchAndHoldHelper from 'mixins/TouchAndHoldHelper';
import KeyToFingerHelper from 'mixins/KeyToFingerHelper';

import ActivityStage from 'components/activity/ActivityStage.vue';

export default {
  mixins: [ActivityManager, TypingActivityHelper, TouchAndHoldHelper, KeyToFingerHelper],
  components: {
    ActivityStage
  },
  data() {
    return {
      activityName: 'Target Practice',
      showBothHands: false,
      displayHand: ''
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    isVisibleLeftHand() {
      return this.showHand('left');
    },
    leftHand() {
      return this.getHand({
        key: this.expectedKey,
        oneHandedTypingHand: this.oneHandedTypingHand,
        side: 'left'
      });
    },
    isVisibleRightHand() {
      return this.showHand('right');
    },
    rightHand() {
      return this.getHand({
        key: this.expectedKey,
        oneHandedTypingHand: this.oneHandedTypingHand,
        side: 'right'
      });
    }
  },

  methods: {
    showHand(hand) {
      return (
        (this.showBothHands || this.displayHand === hand) &&
        (!this.oneHandedTypingHand || this.oneHandedTypingHand === `${hand}-hand`)
      );
    },
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
    },
    getKeyboardConfig() {
      const { characters } = this.activityData.keyboard;

      const {
        keyboard: { keyboardType, roofs }
      } = this.activityData;
      return {
        keys: characters,
        touchAndHold: this.isTouchHold,
        showTargets: true,
        keyboardType,
        roofs
      };
    },

    stepCallback() {
      return this.currentStep;
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds: rounds.map(r => ({
          ...r,
          isTouchHold: this.oneHandedTypingHand ? false : undefined
        })),
        stepIndicator: 'keystrokes'
      };
    },

    startRound() {
      this.enableKeyboard();

      this.setupNewRound({ stepCallback: this.stepCallback });
      this.setActivityContainerHeight();

      if (this.touchAndHold) this.displayHand = this.targetKey === 'j' ? 'left' : 'right';

      if (!this.touchAndHold) {
        this.showBothHands = true;
        this.displayHand = '';
      }
    },

    getHand(params) {
      const { key, oneHandedTypingHand, side } = params;
      const handImages = this[oneHandedTypingHand ? 'oneHandedTypingHands' : 'hands'][side].images;
      return handImages[key]?.svg || handImages.empty.svg;
    },

    handleCorrectKey(attempt) {
      document.getElementById('target-practice-container')?.blur();
      this.captureKeyboardAttemptSegment(attempt, true);

      if (this.isLastStepInRound) {
        this.finishRound();
        return;
      }

      this.sndType();
      this.setCurrentStep({ stepCallback: this.stepCallback });
    },

    finishRound() {
      this.sndBell();

      if (this.isLastRound) {
        this.completeActivity();
      } else {
        this.startRound();
      }
    }
  },
  watch: {
    expectedKey(newVal) {
      if (newVal) {
        let instructions = `Type the ${newVal} key.`;

        this.setCurrentKeyboardingSegment('');
        this.setTotalKeyboardingSegment(newVal);
        this.setScreenReaderAlert(this.getTouchHoldBasedInstructions(instructions));
      }
    }
  }
};
</script>

<style lang="scss" scoped>
#target-practice-container {
  width: 100%;
  height: 100%;
}

.target-practice-container {
  position: relative;
  max-width: 1024px;
  max-height: 480px;
  width: 100%;
  height: 100%;

  .target-practice-image-left,
  .target-practice-image-right {
    position: absolute;
    top: 8%;
    width: 200px;
    height: 250px;
    max-height: 100%;

    .svg-image-container {
      position: absolute;
      height: 100%;
      visibility: hidden;

      svg {
        height: 100%;
      }
    }

    .current-letter {
      visibility: visible;
    }
  }

  .target-practice-image-left {
    left: 100px;
    @media (max-width: 690px) {
      left: 20px;
    }
  }

  .target-practice-image-right {
    right: 100px;
    @media (max-width: 690px) {
      right: 20px;
    }
  }

  .one-hand-typing-hand {
    position: relative;
    left: 50%;
    transform: translateX(-50%);
  }
}
</style>
